import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';

import { AnalysisSectionLayout, SectionBlocks, type SectionBlock } from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { hasContent } from '../../utils/hasContent';

import {
  BreakthroughOpportunities,
  CompetitiveAnalysis,
  CompetitiveLandscapeAndMarketGrowth,
  EmergingTrends,
  EstablishedMarketNorms,
  hasBreakthroughs,
  hasEstablishedNorms,
  MarketWhiteSpace,
} from './components';
import { useStatistaDatasets } from './lib/useStatistaDatasets';
import { findCitedStatistaDatasets } from './lib/statistaCitations';
import { useTranslation } from 'react-i18next';

export const CategoryCompetition = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const title = t('analysisResult.categoryCompetition.categoryCompetition');
  const analysis = useAnalysisFromContext(analysisProp);

  const categoryCompetition = analysis.structuredData?.categoryCompetition;

  const { datasets } = useStatistaDatasets(analysis.id, {
    hasSection: hasContent(categoryCompetition),
  });

  const blocks: SectionBlock[] = [
    {
      key: 'landscape',
      hasData: hasContent([
        categoryCompetition?.overview,
        categoryCompetition?.marketSize?.yoyTrends,
      ]),
      node: (
        <StaggerItem index={0} variant="swipeUp">
          <CompetitiveLandscapeAndMarketGrowth
            overview={categoryCompetition?.overview}
            overviewQuote={categoryCompetition?.landscapeQuote}
            marketSize={categoryCompetition?.marketSize}
            landscapeDatasets={findCitedStatistaDatasets(datasets, categoryCompetition?.overview)}
            marketGrowthDatasets={findCitedStatistaDatasets(
              datasets,
              categoryCompetition?.marketSize?.yoyTrends,
            )}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'whitespace',
      hasData: hasContent(categoryCompetition?.whitespace),
      node: (
        <StaggerItem index={1} variant="swipeUp">
          <MarketWhiteSpace
            whitespace={categoryCompetition?.whitespace}
            citedDatasets={findCitedStatistaDatasets(datasets, categoryCompetition?.whitespace)}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'competitors',
      hasData: hasContent(categoryCompetition?.topCompetitors),
      node: (
        <StaggerItem index={2} variant="swipeUp">
          <CompetitiveAnalysis
            topCompetitors={categoryCompetition?.topCompetitors}
            brandName={analysis.brandName}
            exporting={exporting}
            datasets={datasets}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'norms',
      hasData: hasEstablishedNorms(categoryCompetition?.establishedNorms),
      node: (
        <StaggerItem index={3} variant="swipeUp">
          <EstablishedMarketNorms
            establishedNorms={categoryCompetition?.establishedNorms}
            citedDatasets={findCitedStatistaDatasets(
              datasets,
              categoryCompetition?.establishedNorms,
            )}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'trends',
      hasData: hasContent(categoryCompetition?.emergingTrends),
      node: (
        <StaggerItem index={4} variant="swipeUp">
          <EmergingTrends
            emergingTrends={categoryCompetition?.emergingTrends}
            citedDatasets={findCitedStatistaDatasets(datasets, categoryCompetition?.emergingTrends)}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'breakthroughs',
      hasData: hasBreakthroughs(categoryCompetition?.breakthroughs),
      node: (
        <StaggerItem index={5} variant="swipeUp">
          <BreakthroughOpportunities
            breakthroughs={categoryCompetition?.breakthroughs}
            citedDatasets={findCitedStatistaDatasets(datasets, categoryCompetition?.breakthroughs)}
          />
        </StaggerItem>
      ),
    },
  ];

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_CATEGORY_COMPETITION}
      title={title}
      subtitle={t('analysisResult.categoryCompetition.marketPositionCompetitiveLandscape', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      <SectionBlocks category={title} blocks={blocks} />
    </AnalysisSectionLayout>
  );
};
